import { motion } from "motion/react";
import { SEO } from "../components/seo";
import { PricingCard } from "../components/pricing/pricing-card";
import { DigitalServiceCard } from "../components/pricing/digital-service-card";
import { MonthlyServiceCard } from "../components/pricing/monthly-service-card";
import { PricingSectionHeader } from "../components/pricing/pricing-section-header";
import { 
  websitePackages, 
  webAppPackages, 
  aiAutomation, 
  digitalServices, 
  monthlyServices 
} from "../data/pricing-data";

export function Pricing() {
  const faqs = [
    {
      q: "Why is a 40-50% deposit required?",
      a: "The deposit secures your spot in my development schedule and covers the initial discovery and design phases of your project."
    },
    {
      q: "Do you offer custom packages?",
      a: "Absolutely. If your project doesn't fit into the standard tiers, I'll provide a custom quote based on your specific requirements."
    },
    {
      q: "What is the typical turnaround time?",
      a: "Starter websites take about 1 week. Business websites and web apps typically range from 3-8 weeks depending on complexity."
    },
    {
      q: "Is hosting included in the price?",
      a: "Hosting is a separate ongoing cost. I offer a managed hosting and monitoring service for 40€/month to keep your site running perfectly."
    }
  ];

  return (
    <>
      <SEO 
        title="Pricing - Custom Software & Digital Solutions" 
        description="Transparent pricing for web development, custom software, AI integration, and digital solutions. From 250 EUR starter websites to enterprise systems."
        canonical="/pricing"
      />

      <div className="min-h-screen bg-black text-white">
        {/* Hero Section */}
        <section className="relative overflow-hidden pt-32 pb-20 px-6">
          {/* Animated Background */}
          <div className="absolute inset-0 opacity-30">
            <div className="absolute top-0 left-1/4 w-96 h-96 bg-violet-500 rounded-full blur-3xl animate-pulse" />
            <div className="absolute bottom-0 right-1/4 w-96 h-96 bg-fuchsia-500 rounded-full blur-3xl animate-pulse delay-1000" />
          </div>

          <div className="max-w-4xl mx-auto relative text-center">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6 }}
            >
              <h1 className="text-5xl md:text-7xl font-bold mb-6 bg-gradient-to-r from-violet-400 via-fuchsia-400 to-cyan-400 bg-clip-text text-transparent">
                Custom Software & Digital Solutions
              </h1>
              <p className="text-xl md:text-2xl text-gray-300 leading-relaxed">
                Built for Growth. Priced for Value.
              </p>
              <div className="mt-8 inline-flex items-center gap-2 px-6 py-3 bg-gradient-to-r from-green-500/20 to-emerald-500/20 rounded-full border border-green-500/30">
                <div className="w-2 h-2 rounded-full bg-green-400 animate-pulse" />
                <span className="text-green-400 font-semibold">40-50% deposit required to start</span>
              </div>
            </motion.div>
          </div>
        </section>

        {/* Website Development */}
        <section className="py-20 px-6">
          <div className="max-w-7xl mx-auto">
            <PricingSectionHeader 
              title="Website Development" 
              subtitle="Professional websites that convert visitors into customers" 
            />
            <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
              {websitePackages.map((pkg, index) => (
                <PricingCard key={pkg.name} pkg={pkg} index={index} />
              ))}
            </div>
          </div>
        </section>

        {/* Web Applications */}
        <section className="py-20 px-6 bg-gradient-to-b from-transparent via-violet-950/10 to-transparent">
          <div className="max-w-7xl mx-auto">
            <PricingSectionHeader 
              title="Web Applications" 
              subtitle="Custom software tailored to your business needs" 
            />
            <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
              {webAppPackages.map((pkg, index) => (
                <PricingCard key={pkg.name} pkg={pkg} index={index} />
              ))}
            </div>
          </div>
        </section>

        {/* AI & Automation */}
        <section className="py-20 px-6">
          <div className="max-w-7xl mx-auto">
            <PricingSectionHeader 
              title="AI & Automation" 
              subtitle="Intelligent solutions to streamline your business" 
            />
            <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
              {aiAutomation.map((pkg, index) => (
                <PricingCard key={pkg.name} pkg={pkg} index={index} />
              ))}
            </div>
          </div>
        </section>

        {/* Digital Optimization */}
        <section className="py-20 px-6 bg-gradient-to-b from-transparent via-fuchsia-950/10 to-transparent">
          <div className="max-w-5xl mx-auto">
            <PricingSectionHeader 
              title="Digital Optimization" 
              subtitle="Boost performance and visibility" 
            />
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {digitalServices.map((service, index) => (
                <DigitalServiceCard key={service.name} service={service} index={index} />
              ))}
            </div>
          </div>
        </section>

        {/* Monthly Services */}
        <section className="py-20 px-6">
          <div className="max-w-7xl mx-auto">
            <PricingSectionHeader 
              title="Monthly Services" 
              subtitle="Ongoing support to keep your digital presence thriving" 
            />
            <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
              {monthlyServices.map((service, index) => (
                <MonthlyServiceCard key={service.name} service={service} index={index} />
              ))}
            </div>
          </div>
        </section>

        {/* FAQ Section */}
        <section className="py-20 px-6 bg-gradient-to-b from-transparent via-violet-950/10 to-transparent">
          <div className="max-w-4xl mx-auto">
            <PricingSectionHeader 
              title="Pricing FAQ" 
              subtitle="Common questions about my services and billing" 
            />
            <div className="space-y-4">
              {faqs.map((faq, index) => (
                <motion.div
                  key={index}
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.6, delay: index * 0.1 }}
                  className="bg-gradient-to-br from-violet-950/20 via-fuchsia-950/20 to-cyan-950/20 rounded-xl p-6 border border-violet-500/20"
                >
                  <h3 className="text-lg font-bold mb-2 text-white">{faq.q}</h3>
                  <p className="text-gray-400">{faq.a}</p>
                </motion.div>
              ))}
            </div>
          </div>
        </section>

        {/* CTA Section */}
        <section className="py-20 px-6">
          <div className="max-w-4xl mx-auto">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6 }}
              className="bg-gradient-to-br from-violet-950/40 via-fuchsia-950/40 to-cyan-950/40 rounded-3xl p-12 border border-violet-500/20 text-center"
            >
              <h2 className="text-3xl md:text-4xl font-bold mb-4 text-white">
                Not Sure Which Package Is Right?
              </h2>
              <p className="text-gray-300 text-lg mb-8">
                Let's discuss your project and find the perfect solution for your needs.
              </p>
              <a
                href="/contact"
                className="inline-block px-8 py-4 bg-gradient-to-r from-violet-600 to-fuchsia-600 text-white rounded-xl font-semibold hover:from-violet-500 hover:to-fuchsia-500 transition-all duration-300 shadow-lg shadow-violet-500/50"
              >
                Schedule a Free Consultation
              </a>
            </motion.div>
          </div>
        </section>
      </div>
    </>
  );
}